/**
 * Account Login Page
 * 
 * Shown only after token validation passes.
 * User enters email/password to access account management.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAccountAuth } from '../../contexts/AccountAuthContext';

const AccountLogin = ({ userData, targetPage = 'account', category }) => {
  const navigate = useNavigate();
  const { login } = useAccountAuth();
  
  const [email, setEmail] = useState(userData?.email || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError(userData?.email ? 'Please enter your password' : 'Please enter both email and password');
      return;
    }

    setIsLoading(true);

    try {
      // TODO: Replace with actual API call
      // POST /api/account/login { email, password }
      const result = await login(email, password);
      
      if (result.success) {
        // Navigate based on target page
        if (targetPage === 'plans') {
          navigate('/app/account/plans', { state: { category } });
        } else if (targetPage === 'payment') {
          navigate('/app/account/payment');
        } else if (targetPage === 'cancel') {
          navigate('/app/account/cancel');
        } else {
          navigate('/app/account/dashboard');
        }
      } else {
        setError(result.error || 'Invalid email or password');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-accent to-accent-dark flex items-center justify-center">
            <span className="text-dark font-bold text-2xl">R</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {userData?.name ? `Welcome, ${userData.name}` : 'Welcome Back'}
          </h1>
          <p className="text-gray-400">
            {userData?.email ? 'Enter your password to continue' : 'Sign in to manage your account'}
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-dark-100 rounded-2xl p-6 border border-white/10">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}

            {/* Email Field - Read-only when pre-filled from token */}
            <div>
              <label className="block text-sm text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => !userData?.email && setEmail(e.target.value)}
                  placeholder="Enter your email"
                  readOnly={!!userData?.email}
                  className={`w-full pl-11 pr-4 py-3 bg-dark-200 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-accent transition-colors ${userData?.email ? 'opacity-70 cursor-not-allowed' : ''}`}
                />
              </div>
              {userData?.email && (
                <p className="text-xs text-gray-500 mt-1">Email verified from your session</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-11 pr-12 py-3 bg-dark-200 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-accent transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-gradient-to-r from-accent to-accent-dark text-dark font-semibold rounded-xl hover:shadow-lg hover:shadow-accent/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        {/* User Info (if pre-filled) */}
        {userData?.name && (
          <p className="mt-4 text-center text-gray-500 text-sm">
            Signing in as <span className="text-white">{userData.name}</span>
          </p>
        )}

        {/* Demo Note - Only show when no pre-filled email */}
        {!userData?.email && (
          <p className="mt-6 text-center text-gray-600 text-xs">
            Demo: Enter any email and password to login
          </p>
        )}
      </motion.div>
    </div>
  );
};

export default AccountLogin;
